import { Box, Flex, FlexProps, Text, useToken } from "@chakra-ui/react";
import { useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { MessagesInner, MessagesInnerTypeEnum } from "@nens/rana/api-v1";
import { useRect } from "@nens/react/hooks";
import { makeUnique } from "@nens/shared-utils";

import { PROFILE_BORDER, Profile, ProfileStack } from "@/lib/profile";
import { RANA_POPUP_PADDING_PX } from "@/lib/rana-popup/rana-popup";
import { getCompleteName } from "@/utils/get-complete-name";
import { getPrettyTimeFromNow, getTime } from "@/utils/get-pretty-time";

import { RESOLVED_OPACITY } from "../consts/resolved-opacity";

import {
  COMMENT_MESSAGE_BORDER_WIDTH_PX,
  COMMENT_MESSAGE_P_PX,
  MessageWrapper,
} from "./comment-message";
import { MessageReactions } from "./message-reactions";

const THREAD_STARTER_MESSAGE_INDEX = 0;

interface CommentHeaderProps extends FlexProps {
  resolved: boolean;
  message: MessagesInner;
  removeMessage: () => void;
  editMessage: (body: string) => void;
  resolveMessage: () => void;
  onToggleReaction: (emoji: string) => void;
}

export const CommentHeader = ({
  resolved,
  message,
  removeMessage,
  resolveMessage,
  editMessage,
  onToggleReaction,
  ...rest
}: CommentHeaderProps) => {
  const { creator } = message;

  // Bit of a hassle to calculate the height of the header so that we can add an empty box with the same height
  // to make sure the popup is positioned correctly
  // (we want it to be positioned based on the top of the header, not the top of the first message)
  const MB = 2;
  const PT = 4;
  const PB = 4;
  const [MB_PX, PT_PX, PB_PX] = useToken("sizes", [MB, PT, PB]);
  const heightRef = useRef<HTMLDivElement>(null);
  const { height } = useRect(heightRef);
  const totalWrapperHeight = `calc(${height}px + ${PT_PX} + ${PB_PX})`;
  const emptyBoxHeight = `calc(${totalWrapperHeight} - ${RANA_POPUP_PADDING_PX}px * 2 - ${MB_PX})`; // Take off the vertical padding of the popup and the margin bottom to get the height that we need for the empty box

  return (
    <>
      <Box h={emptyBoxHeight} />
      <MessageWrapper
        message={message}
        editMessage={editMessage}
        removeMessage={removeMessage}
        resolved={resolved}
        resolveMessage={resolveMessage}
        top={0}
        px={`${RANA_POPUP_PADDING_PX}px`}
        gap={2}
        backgroundColor="semantic.info.background"
        borderBottomWidth="base"
        borderColor="brand.glacier"
        rounded="base"
        overflow="hidden"
        zIndex={0}
        pt={PT}
        pb={PB}
        position="absolute"
        left={0}
        opacity={resolved ? RESOLVED_OPACITY : undefined}
        mb={MB}
        {...rest}
      >
        <Profile boxSize={8} user={creator} />
        <Box ref={heightRef} w="full">
          <Flex align="center" justify="space-between">
            <MessageWrapper.CreatorName creator={creator} />
            <MessageWrapper.Actions />
          </Flex>

          <Text pr={COMMENT_MESSAGE_P_PX}>
            <MessageWrapper.Text />
          </Text>

          <MessageReactions message={message} onToggleReaction={onToggleReaction} />

          <Text ml="auto" float="right" textStyle="xs" color="brand.grey.600">
            <MessageWrapper.MessageTime
              message={message}
              getPrettyTimeFunc={getPrettyTimeFromNow}
            />
          </Text>
        </Box>
      </MessageWrapper>
    </>
  );
};

interface CommentMessageProps extends FlexProps {
  message: MessagesInner;
  lastMessage: MessagesInner | undefined;
  nextMessage: MessagesInner | undefined;
  editMessage: (body: string) => void;
  removeMessage: () => void;
  onToggleReaction: (emoji: string) => void;
}

export const CommentMessage = ({
  message,
  lastMessage,
  nextMessage,
  removeMessage,
  editMessage,
  onToggleReaction,
  ...rest
}: CommentMessageProps) => {
  const intl = useIntl();
  const { creator, type } = message;
  const name = `${creator.givenName} ${creator.familyName}`;
  if (type) {
    const textMap: Record<MessagesInnerTypeEnum, string> = {
      resolved: intl.formatMessage(
        {
          id: "comment.resolved",
          defaultMessage: "{name} has resolved this comment.",
        },
        { name }
      ),
      unresolved: intl.formatMessage(
        {
          id: "comment.unresolved",
          defaultMessage: "{name} has unresolved this comment.",
        },
        { name }
      ),
    };

    return (
      <Flex p={2} gap={2} {...rest}>
        <Text as="i" fontSize="sm" color="brand.grey.600">
          {textMap[type]}
        </Text>
      </Flex>
    );
  }

  const getMessageSameCreator = (
    message: MessagesInner,
    otherMessage: MessagesInner | undefined
  ) => {
    if (otherMessage?.type) return false;
    return message.creator.id
      ? otherMessage?.creator.id === message.creator.id
      : otherMessage?.creator.email === message.creator.email;
  };

  const lastMessageSameCreator = getMessageSameCreator(message, lastMessage);
  const displayTime = shouldDisplayTime(message, nextMessage);

  // Default message case
  return (
    <MessageWrapper
      message={message}
      editMessage={editMessage}
      removeMessage={removeMessage}
      gap={2}
      w="full"
      mr={1}
      {...rest}
    >
      <Box boxSize={8} mx={lastMessageSameCreator ? PROFILE_BORDER : undefined}>
        {!lastMessageSameCreator && <Profile boxSize={8} user={creator} />}
      </Box>

      <Box w="full" overflow="hidden">
        <Flex
          justify="space-between"
          borderWidth={COMMENT_MESSAGE_BORDER_WIDTH_PX}
          rounded="base"
          p={COMMENT_MESSAGE_P_PX}
          bg="brand.cloudy"
          mb={displayTime ? 0 : 2}
          fontSize="16px"
        >
          <Box>
            {!lastMessageSameCreator && <MessageWrapper.CreatorName creator={creator} />}
            <MessageWrapper.Text />
          </Box>

          <MessageWrapper.Actions />
        </Flex>
        {displayTime && (
          <Text mb={2} ml={2} float="right" textStyle="xs" color="brand.grey.600">
            <MessageWrapper.MessageTime message={message} getPrettyTimeFunc={getTime} />
          </Text>
        )}
        <MessageReactions message={message} onToggleReaction={onToggleReaction} />
      </Box>
    </MessageWrapper>
  );
};

interface CommentThreadCondensedProps {
  messages: MessagesInner[];
  onClick?: () => void;
  resolved: boolean;
  threadId: string;
  isSelected: boolean;
  removeCommentThread: (threadId: string) => void;
  editCommentMessage: (messageIndex: number, body: string) => void;
  resolveComment: (type: "resolve" | "unresolve") => void;
  toggleCommentReaction: (messageIndex: number, emoji: string) => void;
}

export const CommentThreadCondensed = ({
  messages,
  onClick,
  resolved,
  threadId,
  isSelected,
  removeCommentThread,
  editCommentMessage,
  resolveComment,
  toggleCommentReaction,
}: CommentThreadCondensedProps) => {
  const threadStarter = messages[THREAD_STARTER_MESSAGE_INDEX];
  const { creator } = threadStarter;
  const displayTime = shouldDisplayTime(threadStarter, undefined);
  const numberOfReplies = Math.max(
    0,
    messages.filter((message) => !message.deleted && message.body !== undefined).length - 1
  );
  const showNumberOfReplies = numberOfReplies > 0;
  const editMessage = (body: string) => editCommentMessage(THREAD_STARTER_MESSAGE_INDEX, body);

  const contributors = useMemo(
    () =>
      makeUnique(
        messages.map((message) => message.creator),
        (creator) => creator.id + creator.email // Use id and email incase of api key users (they can have the same id but different email's)
      ),
    [messages]
  );

  return (
    <MessageWrapper
      message={threadStarter}
      editMessage={editMessage}
      removeMessage={() => removeCommentThread(threadId)}
      resolved={resolved}
      resolveMessage={() => resolveComment(resolved ? "unresolve" : "resolve")}
      flexDirection="column"
      gap={1}
      py={2}
      px={4}
      onClick={onClick}
      cursor={onClick ? "pointer" : undefined}
      opacity={resolved ? RESOLVED_OPACITY : undefined}
      bg={isSelected ? "brand.selected" : undefined}
      borderBottomWidth="base"
      _hover={{ bg: "button.tertiary.hover" }}
    >
      <Flex justifyContent="space-between">
        <ProfileStack users={contributors} />
        <MessageWrapper.Actions
          mr={`calc(${COMMENT_MESSAGE_P_PX} + ${COMMENT_MESSAGE_BORDER_WIDTH_PX})`}
        />
      </Flex>
      <Text as="b">{getCompleteName(creator)}</Text>

      <Box overflow="hidden">
        <Box color="brand.deep_sea">
          <MessageWrapper.Text />
        </Box>
        <MessageReactions
          message={threadStarter}
          onToggleReaction={(emoji) => toggleCommentReaction(THREAD_STARTER_MESSAGE_INDEX, emoji)}
        />
        {displayTime && (
          <Box fontSize="xs" mt={2}>
            <Text display="flex" gap={2} float="left" color="brand.clear_creek">
              {showNumberOfReplies && (
                <Box as="span" textDecoration="underline">
                  {numberOfReplies}{" "}
                  {numberOfReplies === 1 ? (
                    <FormattedMessage
                      id="publication.comment.thread.reply"
                      defaultMessage="reply"
                    />
                  ) : (
                    <FormattedMessage
                      id="publication.comment.thread.replies"
                      defaultMessage="replies"
                    />
                  )}
                </Box>
              )}
              <Box as="span">
                <MessageWrapper.MessageTime
                  message={threadStarter}
                  getPrettyTimeFunc={getPrettyTimeFromNow}
                />
              </Box>
              {resolved && <FormattedMessage id="comment.resolved" defaultMessage="(resolved)" />}
            </Text>
          </Box>
        )}
      </Box>
    </MessageWrapper>
  );
};

// Shared utility functions
const getMessageSameCreator = (message: MessagesInner, otherMessage: MessagesInner | undefined) => {
  if (otherMessage?.type) return false;
  return message.creator.id
    ? otherMessage?.creator.id === message.creator.id
    : otherMessage?.creator.email === message.creator.email;
};

const shouldDisplayTime = (message: MessagesInner, nextMessage: MessagesInner | undefined) => {
  const nextMessageSameCreator = getMessageSameCreator(message, nextMessage);
  return (
    message.edited ||
    !nextMessageSameCreator ||
    getTime(message.createdAt) !== getTime(nextMessage?.createdAt)
  );
};
